import messaging from '@react-native-firebase/messaging';
import { PermissionsAndroid, Platform } from 'react-native';
import { Logger } from '../utils/logger';
import { apiClient } from './api';
import { getDevicePosition } from './locationService';

export function currentPlatformName(): string {
  return Platform.OS === 'ios' ? 'iOS' : 'Android';
}

// @react-native-firebase/messaging is native-only: on web every messaging()
// call throws "firebase module not installed". Guarding here keeps the web
// build quiet without each caller needing to know FCM exists only on device.
const FCM_SUPPORTED = Platform.OS === 'android' || Platform.OS === 'ios';

/**
 * What this handset calls itself, from React Native's own Platform.constants.
 *
 * Deliberately no react-native-device-info: that is a native dependency needing a
 * rebuild, and the constants React Native already exposes answer the question the
 * office actually asks — "which of this parent's two phones is this?".
 *
 * The two platforms expose different things, and it is worth knowing which:
 *   Android gives a real make and model ("Samsung SM-G991B").
 *   iOS exposes no model at all here, so it reports its system version instead.
 */
export function currentDeviceName(): string | undefined {
  try {
    const c: any = Platform.constants ?? {};

    if (Platform.OS === 'android') {
      // Brand is often already the first word of Model on some vendors, so the
      // two are only joined when Model does not already start with the brand.
      const brand: string = (c.Manufacturer || c.Brand || '').toString().trim();
      const model: string = (c.Model || '').toString().trim();

      if (!model) return brand || undefined;
      if (!brand) return model;

      return model.toLowerCase().startsWith(brand.toLowerCase())
        ? model
        : `${brand} ${model}`;
    }

    const systemName: string = (c.systemName || 'iOS').toString().trim();
    const osVersion: string = (c.osVersion || '').toString().trim();

    return osVersion ? `${systemName} ${osVersion}` : systemName;
  } catch {
    // Never worth failing a registration for.
    return undefined;
  }
}

/** The OS version alone, when the platform reports one separately. */
export function currentOsVersion(): string | undefined {
  try {
    const c: any = Platform.constants ?? {};

    if (Platform.OS === 'android') {
      // Release is the human version ("14"); Version is the API level (34).
      const release = c.Release ? String(c.Release).trim() : '';
      return release || (Platform.Version ? String(Platform.Version) : undefined);
    }

    const osVersion = c.osVersion ? String(c.osVersion).trim() : '';
    return osVersion || undefined;
  } catch {
    return undefined;
  }
}

// Saves the token on the backend (AppNotificationController), which links it to
// whichever parent is currently logged in. The server sends to it later through
// the Firebase Admin SDK. Called on every launch, so it doubles as the
// per-device "last login" the office sees. A failure here must never block the
// rest of the app.
export async function registerAppNotification(
  token: string,
  studentIds: string[] = [],
): Promise<void> {
  try {
    Logger.info('FCM', `Registering token with backend (POST /AppNotification/register, platform=${currentPlatformName()}, token=${token.slice(0, 12)}…)`);
    // Asked for, never depended on. A parent who declines location still
    // registers for push exactly as before — getDevicePosition returns undefined
    // rather than throwing, so there is nothing to handle here.
    const position = await getDevicePosition();

    await apiClient.post('/AppNotification/register', {
      FcmToken: token,
      Platform: currentPlatformName(),
      DeviceName: currentDeviceName(),
      OsVersion: currentOsVersion(),
      Latitude: position?.latitude,
      Longitude: position?.longitude,
      // Which children this handset is signed in for.
      //
      // Sent at login because that is when the app knows them. The server stores
      // them in skl.AppNotificationStudent, which is what lets ONE token answer
      // for several students - a parent with two children has one phone, one
      // token, and two links.
      StudentIds: studentIds,
    });
    Logger.info(
      'FCM',
      `✅ Token registered with backend for ${studentIds.length} student(s)`
      + `${position ? ' with a location' : ' without a location'}`,
    );
  } catch (error) {
    Logger.warn('FCM', '❌ Failed to register token with backend', error);
  }
}

/**
 * Tells the server this handset is no longer signed in.
 *
 * Must run BEFORE the session is torn down: the endpoint is authorised, so once
 * the access token is gone there is no way to make this call. Without it a
 * signed-out phone keeps its row marked active and keeps receiving push about
 * somebody's child - and the Devices tab reports it as reachable.
 */
export async function deactivateCurrentDevice(): Promise<void> {
  if (!FCM_SUPPORTED) return;
  try {
    // Read the token fresh rather than plumbing it through the auth context;
    // Firebase already holds the one this device registered with.
    const token = await messaging().getToken();
    if (!token) return;

    await apiClient.delete(`/AppNotification/${encodeURIComponent(token)}`);
    Logger.info('FCM', 'Device deactivated on the server');
  } catch (error) {
    // Sign-out must complete regardless. A stale row is better than a parent
    // who cannot sign out.
    Logger.warn('FCM', 'Could not deactivate the device on the server', error);
  }
}

export async function requestNotificationPermission(): Promise<boolean> {
  if (!FCM_SUPPORTED) {
    Logger.info('FCM', `Skipped — platform '${Platform.OS}' has no native FCM (expected on web)`);
    return false;
  }
  try {
    Logger.info('FCM', `Step 1/2: requesting permission (platform=${Platform.OS}, osVersion=${Platform.Version})`);
    if (Platform.OS === 'android' && Platform.Version >= 33) {
      const granted = await PermissionsAndroid.request(
        PermissionsAndroid.PERMISSIONS.POST_NOTIFICATIONS
      );
      Logger.info('FCM', `Android 13+ POST_NOTIFICATIONS dialog result: '${granted}'`);
      if (granted !== PermissionsAndroid.RESULTS.GRANTED) {
        Logger.warn('FCM', `❌ POST_NOTIFICATIONS denied ('${granted}'). If 'never_ask_again', enable it in Settings → Apps → notifications.`);
        return false;
      }
    }

    const authStatus = await messaging().requestPermission();
    const enabled =
      authStatus === messaging.AuthorizationStatus.AUTHORIZED ||
      authStatus === messaging.AuthorizationStatus.PROVISIONAL;

    Logger.info('FCM', `Step 2/2: Firebase requestPermission → authStatus=${authStatus} (0=DENIED 1=AUTHORIZED 2=PROVISIONAL) → enabled=${enabled}`);
    if (!enabled) {
      Logger.warn('FCM', `❌ Permission not granted, authStatus=${authStatus}`);
    }

    return enabled;
  } catch (error) {
    // Firebase native app may not be initialized on this device (e.g. missing/
    // outdated Google Play Services). FCM being unavailable must never break
    // the rest of the app.
    Logger.warn('FCM', 'requestNotificationPermission failed — Firebase app may not be initialized', error);
    return false;
  }
}

// Debug-only console output: makes the token easy to copy while wiring up
// Firebase Console "send test message" or checking what got registered
// server-side. Remove/quiet this once FCM is fully wired end-to-end.
function logToken(label: string, token: string) {
  console.log(`==================== ${label} ====================`);
  console.log(token);
  console.log('='.repeat(20 + label.length + 22));
  Logger.info('FCM', `${label}: ${token}`);
}

const TOKEN_RETRY_DELAYS_MS = [2_000, 5_000, 10_000];

// SERVICE_NOT_AVAILABLE / IOException from getToken() is a transient failure of
// the device's connection to Google's FCM backend (flaky Wi-Fi, GCM socket not
// up yet right after app start). Google's guidance is to retry with exponential
// backoff rather than treat it as fatal — one cold attempt at login regularly
// loses the token on real networks.
export async function getFcmToken(): Promise<string | null> {
  if (!FCM_SUPPORTED) return null;

  for (let attempt = 0; attempt <= TOKEN_RETRY_DELAYS_MS.length; attempt++) {
    try {
      Logger.info('FCM', `Fetching device token from Firebase… (attempt ${attempt + 1}/${TOKEN_RETRY_DELAYS_MS.length + 1})`);
      const token = await messaging().getToken();
      logToken('FCM TOKEN', token);
      return token;
    } catch (error) {
      const isLastAttempt = attempt === TOKEN_RETRY_DELAYS_MS.length;
      if (isLastAttempt) {
        Logger.error('FCM', '❌ Failed to get FCM token after all retries — check google-services.json / Google Play Services / network on this device', error);
        return null;
      }
      const delay = TOKEN_RETRY_DELAYS_MS[attempt];
      Logger.warn('FCM', `Token fetch failed (likely transient, e.g. SERVICE_NOT_AVAILABLE) — retrying in ${delay / 1000}s`, error);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  return null;
}

const NOOP_UNSUBSCRIBE = () => { };

export function onFcmTokenRefresh(callback: (token: string) => void): () => void {
  if (!FCM_SUPPORTED) return NOOP_UNSUBSCRIBE;
  try {
    const unsubscribe = messaging().onTokenRefresh((token) => {
      logToken('FCM TOKEN REFRESHED', token);
      callback(token);
    });
    Logger.info('FCM', 'Listener active: onTokenRefresh');
    return unsubscribe;
  } catch (error) {
    Logger.warn('FCM', 'onFcmTokenRefresh setup failed — Firebase app may not be initialized', error);
    return NOOP_UNSUBSCRIBE;
  }
}

export function onForegroundMessage(callback: (message: any) => void): () => void {
  if (!FCM_SUPPORTED) return NOOP_UNSUBSCRIBE;
  try {
    const unsubscribe = messaging().onMessage(async (remoteMessage) => {
      Logger.info('FCM', `📩 Foreground message received: ${JSON.stringify(remoteMessage)}`);
      callback(remoteMessage);
    });
    Logger.info('FCM', 'Listener active: onMessage (foreground)');
    return unsubscribe;
  } catch (error) {
    Logger.warn('FCM', 'onForegroundMessage setup failed — Firebase app may not be initialized', error);
    return NOOP_UNSUBSCRIBE;
  }
}

export function onNotificationOpenedFromBackground(callback: (message: any) => void): () => void {
  if (!FCM_SUPPORTED) return NOOP_UNSUBSCRIBE;
  try {
    const unsubscribe = messaging().onNotificationOpenedApp((remoteMessage) => {
      Logger.info('FCM', `📩 Notification opened app from background: ${JSON.stringify(remoteMessage)}`);
      callback(remoteMessage);
    });
    Logger.info('FCM', 'Listener active: onNotificationOpenedApp (background tap)');
    return unsubscribe;
  } catch (error) {
    Logger.warn('FCM', 'onNotificationOpenedFromBackground setup failed — Firebase app may not be initialized', error);
    return NOOP_UNSUBSCRIBE;
  }
}

export async function getInitialNotification(): Promise<any | null> {
  if (!FCM_SUPPORTED) return null;
  try {
    const remoteMessage = await messaging().getInitialNotification();
    if (remoteMessage) {
      Logger.info('FCM', `App opened from quit state by notification: ${JSON.stringify(remoteMessage)}`);
    }
    return remoteMessage;
  } catch (error) {
    Logger.warn('FCM', 'getInitialNotification failed — Firebase app may not be initialized', error);
    return null;
  }
}
